import React from 'react';
import ImageUploadBox from '@/components/common/ImageUploadBox';
import { Select } from '@/components/common/Select';

export interface ContentUploadFormProps {
  data: Api.Application.Step2Request;
  onChange: (data: Api.Application.Step2Request) => void;
}

const CATEGORY_OPTIONS = [
  { label: '预告片 / 宣发视频', value: 'APPLICATION_TRAILER' },
  { label: '宣传海报 / 剧照资料', value: 'APPLICATION_POSTER' },
];

export default function ContentUploadForm({ data, onChange }: ContentUploadFormProps) {
  // 核心独立封面 ID (cover_asset_id 或 attachments 中 category 为 APPLICATION_COVER)
  const coverAttachment = (data.attachments || []).find((a) => a.category === 'APPLICATION_COVER') as any;
  const coverAssetId =
    data.cover_asset_id ||
    coverAttachment?.upload_asset_id ||
    '';
  const coverUrl =
    (data as any).cover_url ||
    coverAttachment?.upload_asset?.access_url ||
    coverAttachment?.access_url ||
    '';
  const coverValue = coverUrl || coverAssetId;

  // 其他宣发物料列表（过滤掉 APPLICATION_COVER）
  const otherAttachments = (data.attachments || []).filter((a) => a.category !== 'APPLICATION_COVER');

  const handleCoverChange = (assetId: string) => {
    const nextAttachments = (data.attachments || []).filter((a) => a.category !== 'APPLICATION_COVER');
    if (assetId) {
      nextAttachments.unshift({
        category: 'APPLICATION_COVER',
        upload_asset_id: assetId,
        display_name: '发行申请封面海报',
        visibility: 'FRONTEND_VISIBLE',
        sort_no: 0,
      });
    }
    onChange({
      ...data,
      cover_asset_id: assetId,
      attachments: nextAttachments,
    });
  };

  const updateOtherItem = (index: number, updatedField: Partial<Api.Application.Step2AttachmentRequest>) => {
    const list = [...otherAttachments];
    list[index] = { visibility: 'FRONTEND_VISIBLE', ...list[index], ...updatedField };

    const coverItem = coverAssetId
      ? [{
        category: 'APPLICATION_COVER',
        upload_asset_id: coverAssetId,
        display_name: '发行申请封面海报',
        visibility: 'FRONTEND_VISIBLE',
        sort_no: 0,
      }]
      : [];
    onChange({ ...data, attachments: [...coverItem, ...list] });
  };

  const addOtherItem = () => {
    const list = [
      ...otherAttachments,
      {
        category: 'APPLICATION_TRAILER',
        display_name: '预告片宣发视频',
        upload_asset_id: '',
        visibility: 'FRONTEND_VISIBLE',
        sort_no: otherAttachments.length + 1,
      },
    ];
    const coverItem = coverAssetId
      ? [{
        category: 'APPLICATION_COVER',
        upload_asset_id: coverAssetId,
        display_name: '发行申请封面海报',
        visibility: 'FRONTEND_VISIBLE',
        sort_no: 0,
      }]
      : [];
    onChange({ ...data, attachments: [...coverItem, ...list] });
  };

  const removeOtherItem = (index: number) => {
    const list = otherAttachments.filter((_, i) => i !== index);
    const coverItem = coverAssetId
      ? [{
        category: 'APPLICATION_COVER',
        upload_asset_id: coverAssetId,
        display_name: '发行申请封面海报',
        visibility: 'FRONTEND_VISIBLE',
        sort_no: 0,
      }]
      : [];
    onChange({ ...data, attachments: [...coverItem, ...list] });
  };

  return (
    <div className="space-y-6">
      {/* 独立封面图 (APPLICATION_COVER) 卡片 */}
      <div className="p-6 rounded-xl bg-primary-50/40 border border-primary-200/70 space-y-4">
        <div className="flex items-center justify-between">
          <label className="text-base font-bold text-foreground-900 flex items-center gap-2">
            <i className="ri-image-line text-primary-600 text-lg"></i>
            发行申请项目封面 <span className="text-red-500">*</span>
          </label>
        </div>
        <ImageUploadBox
          value={coverValue}
          onChange={handleCoverChange}
          scene="APPLICATION"
          accept="image/*"
          placeholder="点击或拖拽上传项目主封面图片"
          subPlaceholder="支持 JPG、PNG 高清海报图片，上传后开启草稿保存"
        />
      </div>

      {/* 宣发资料与视频附件列表 */}
      <div className="space-y-5 pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-foreground-800">宣发资料与视频附件（可选）</h4>
          <span className="text-xs md:text-sm text-foreground-400">预告片、剧照海报包等</span>
        </div>

        {otherAttachments.map((item, idx) => {
          const isVideoCategory = item.category === 'APPLICATION_TRAILER';
          const acceptType = isVideoCategory ? 'video/*,.mp4,.mov' : 'image/*,.zip,.rar,.pdf';
          const itemUrl = (item as any).upload_asset?.access_url || (item as any).access_url || item.upload_asset_id || '';

          return (
            <div
              key={idx}
              className="p-5 rounded-xl bg-white border border-gray-200/80 shadow-2xs space-y-5 relative group"
            >
              <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                <span className="text-sm font-bold text-foreground-800">
                  宣发资料附件 #{idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeOtherItem(idx)}
                  className="text-xs md:text-sm text-red-500 hover:text-red-700 font-medium cursor-pointer transition-colors"
                >
                  <i className="ri-delete-bin-line mr-1"></i>删除此资料
                </button>
              </div>

              {/* 分类 & 名称下拉/输入框 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div>
                  <label className="block text-sm font-bold text-foreground-800 mb-2">
                    资料分类 <span className="text-red-500">*</span>
                  </label>
                  <Select
                    size="md"
                    value={item.category || 'APPLICATION_TRAILER'}
                    onChange={(val) => updateOtherItem(idx, { category: String(val) })}
                    options={CATEGORY_OPTIONS}
                    placeholder="选择资料分类"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-foreground-800 mb-2">
                    资料名称说明
                  </label>
                  <input
                    type="text"
                    value={item.display_name || ''}
                    onChange={(e) => updateOtherItem(idx, { display_name: e.target.value })}
                    placeholder="例：主预告片 / 宣发海报包"
                    className="w-full h-[46px] px-4 rounded-lg bg-white border border-background-300/80 text-base text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all"
                  />
                </div>
              </div>

              {/* 上传文件控件 */}
              <div>
                <label className="block text-sm font-bold text-foreground-800 mb-2">
                  上传资料文件
                </label>
                <ImageUploadBox
                  value={itemUrl}
                  onChange={(assetId) => updateOtherItem(idx, { upload_asset_id: assetId })}
                  scene="APPLICATION"
                  accept={acceptType}
                  isVideo={isVideoCategory}
                  placeholder={isVideoCategory ? '点击或拖拽上传预告片 / 宣发视频 (MP4 / MOV)' : '点击或拖拽上传海报图片或剧照压缩包'}
                  subPlaceholder={isVideoCategory ? '支持 MP4、MOV 格式视频预览与播放' : '支持 JPG、PNG、PDF 及压缩包'}
                />
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={addOtherItem}
          className="w-full py-3 rounded-lg border border-dashed border-primary-300 text-primary-600 hover:bg-primary-50/50 text-sm font-semibold transition-all cursor-pointer flex items-center justify-center gap-2"
        >
          <i className="ri-add-line text-lg"></i> 添加宣发视频 / 剧照海报资料
        </button>
      </div>
    </div>
  );
}
